import React from 'react';
import Modal from '../Modal';
import { Fournisseur } from '../../types/weighing';
import { BuildingOfficeIcon, MapPinIcon, EnvelopeIcon, UserIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';

interface SupplierDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplier: Fournisseur;
}

const SupplierDetailsModal: React.FC<SupplierDetailsModalProps> = ({ 
  isOpen, 
  onClose, 
  supplier 
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  const getCooperativeDisplay = (cooperative: string | undefined) => {
    return cooperative || 'Non spécifiée';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Détails du fournisseur" size="lg">
      <div className="space-y-6">
        {/* En-tête avec nom */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <BuildingOfficeIcon className="h-8 w-8 text-orange-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">{supplier.nom}</h3>
              <p className="text-sm text-gray-600">Fournisseur</p>
            </div>
          </div>
        </div>

        {/* Informations principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Contact et Coopérative */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Contact principal
              </label>
              <div className="flex items-center space-x-2">
                <UserIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">
                  {supplier.contact || 'Non spécifié'}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Coopérative
              </label>
              <div className="flex items-center space-x-2">
                <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">
                  {getCooperativeDisplay(supplier.cooperative)}
                </span>
              </div>
            </div>
          </div>

          {/* Email et Certifications */}
          <div className="space-y-4">
            {supplier.email && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Email
                </label>
                <div className="flex items-center space-x-2">
                  <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-900">{supplier.email}</span>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Certifications
              </label>
              <div className="flex items-center space-x-2">
                <CheckBadgeIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">
                  {supplier.certifications && supplier.certifications.length > 0 
                    ? supplier.certifications.join(', ') 
                    : 'Aucune certification'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Adresse */}
        {supplier.adresse && (
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-2">
              Adresse
            </label>
            <div className="flex items-start space-x-2 p-3 bg-gray-50 rounded-lg">
              <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5" />
              <span className="text-sm text-gray-900">{supplier.adresse}</span>
            </div>
          </div>
        )}

        {/* Informations système */}
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-500 mb-3">Informations système</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">ID :</span>
              <span className="ml-2 text-gray-900 font-mono">{supplier.id}</span>
            </div>
            {supplier.created_at && (
              <div>
                <span className="text-gray-500">Créé le :</span>
                <span className="ml-2 text-gray-900">{formatDate(supplier.created_at)}</span>
              </div>
            )}
            {supplier.updated_at && (
              <div>
                <span className="text-gray-500">Modifié le :</span>
                <span className="ml-2 text-gray-900">{formatDate(supplier.updated_at)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Bouton de fermeture */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SupplierDetailsModal;
